import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DialogHeaderActions from '../DialogHeaderActions';
import { downloadChatAttachment } from './ChatAttachmentPreview';
import { CHAT_HTML_CONTENT_TYPE_WITH_UTF8_CHARSET, CHAT_HTML_RESIZE_MESSAGE_TYPE, DEFAULT_CHAT_HTML_FILE_NAME } from './ChatHtmlConstants';

export const MAX_CHAT_HTML_LENGTH = 262144;

export function buildChatHtmlDocument(html: string, scripts: boolean): string {
	const policy = `default-src 'none'; script-src ${scripts ? "'unsafe-inline'" : "'none'"}; style-src 'unsafe-inline'; img-src data:; connect-src 'none'; font-src 'none'; object-src 'none'; frame-src 'none'; worker-src 'none'; base-uri 'none'; form-action 'none'`;
	const resize = scripts ? `<script>
(() => {
  let pending = false;
  const report = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      parent.postMessage({ type: '${CHAT_HTML_RESIZE_MESSAGE_TYPE}', height: Math.max(document.body.scrollHeight, document.body.offsetHeight) }, '*');
    });
  };
  new ResizeObserver(report).observe(document.body);
  report();
})();
</script>` : '';
	return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${policy}"><meta name="viewport" content="width=device-width, initial-scale=1"><style>html,body{margin:0;padding:0;background-color:#fff}body{font:14px system-ui,sans-serif;overflow-wrap:anywhere;color:#1f2937}*{box-sizing:border-box}</style></head><body>${html}${resize}</body></html>`;
}

/** Agent HTML runs in an opaque origin and receives no portal credentials or capabilities. */
const HtmlFrame = React.memo(function HtmlFrame({ html, scripts, expanded = false }: { html: string; scripts: boolean; expanded?: boolean }) {
	const frame = useRef<HTMLIFrameElement>(null);
	const [height, setHeight] = useState(240);
	const oversized = html.length > MAX_CHAT_HTML_LENGTH;
	const document = useMemo(() => oversized ? '' : buildChatHtmlDocument(html, scripts), [html, scripts, oversized]);
	useEffect(() => {
		setHeight(240);
		const resize = (event: MessageEvent) => {
			if (!scripts || !frame.current || event.source !== frame.current.contentWindow || event.data?.type !== CHAT_HTML_RESIZE_MESSAGE_TYPE) return;
			const size = event.data.height;
			if (typeof size === 'number' && Number.isFinite(size)) setHeight(Math.max(80, Math.min(800, Math.ceil(size))));
		};
		window.addEventListener('message', resize);
		return () => window.removeEventListener('message', resize);
	}, [document, scripts]);
	if (oversized) return <span>HTML content exceeds the display limit.</span>;
	return <iframe
		ref={frame}
		title={expanded ? 'Conversation HTML preview' : 'Conversation interactive content'}
		sandbox={scripts ? 'allow-scripts' : ''}
		referrerPolicy="no-referrer"
		allow="camera 'none'; microphone 'none'; geolocation 'none'; clipboard-read 'none'; clipboard-write 'none'; payment 'none'"
		srcDoc={document}
		style={{ width: '100%', minWidth: 220, height: expanded ? '100%' : height, border: 0, display: 'block', marginTop: expanded ? 0 : 8 }}
	/>;
});

const downloadHtmlDocument = (html: string, fileName: string) => {
	const url = URL.createObjectURL(new Blob([html], { type: CHAT_HTML_CONTENT_TYPE_WITH_UTF8_CHARSET }));
	downloadChatAttachment(url, fileName);
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const ChatHtmlContent = React.memo(function ChatHtmlContent({ html, scripts, fileName, onDownload }: {
	html: string;
	scripts: boolean;
	fileName?: string;
	onDownload?: () => void | Promise<void>;
}) {
	const [preview, setPreview] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);
	const attachmentName = fileName ?? DEFAULT_CHAT_HTML_FILE_NAME;
	const handleDownload = () => {
		if (onDownload) {
			void onDownload();
		} else {
			downloadHtmlDocument(html, attachmentName);
		}
	};

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
				<Button size="small" onClick={() => setPreview(true)}>Open preview</Button>
				<Box sx={{ flex: 1 }} />
				<Tooltip title={`Download ${attachmentName}`}>
					<IconButton size="small" aria-label={`Download ${attachmentName}`} onClick={handleDownload}>
						<FileDownloadIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
			<HtmlFrame html={html} scripts={scripts} />
			<Dialog
				open={preview}
				onClose={() => setPreview(false)}
				fullWidth
				maxWidth="lg"
				fullScreen={isMaximized}
				PaperProps={{ sx: isMaximized ? { height: '100vh', maxHeight: '100vh' } : { height: 'min(860px, 92vh)' } }}
			>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
						{fileName ?? 'HTML preview'}
					</Box>
					<DialogHeaderActions
						isMaximized={isMaximized}
						onToggleMaximized={() => setIsMaximized((value) => !value)}
						onClose={() => setPreview(false)}
					>
						<Tooltip title={`Download ${attachmentName}`}>
							<IconButton size="small" aria-label={`Download ${attachmentName}`} onClick={handleDownload}>
								<FileDownloadIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</DialogHeaderActions>
				</DialogTitle>
				<DialogContent sx={{ height: isMaximized ? '100%' : '75vh', p: 1 }}>
					{preview && <HtmlFrame html={html} scripts={scripts} expanded />}
				</DialogContent>
			</Dialog>
		</Box>
	);
});

export default ChatHtmlContent;
